import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import { Actions as ActionsIcon, PlayCircle } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type { LibraryVideoLinkItem } from "../types/library.types";
import { LibraryShareType } from "../types/library.types";
import { libraryApi } from "../services/library.api";
import AddVideoLinkModal from "../components/AddVideoLinkModal";
import ConfirmModal from "@/components/ui/confirm-modal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { LibraryBasePath } from "./LibraryPage";
import styles from "./LibraryPage.module.scss";
import documentsStyles from "./DocumentsPage.module.scss";

interface VideosPageProps {
  basePath?: LibraryBasePath;
}

const VideosPage: React.FC<VideosPageProps> = ({
  basePath = "admin-library",
}) => {
  const prefix = `/dashboard/${basePath}`;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.LIBRARY_CREATE);
  const canDelete = hasPermission(PERMISSIONS.LIBRARY_DELETE);
  const isClientLibrary = basePath === "library";
  const [videos, setVideos] = useState<LibraryVideoLinkItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalVideos, setTotalVideos] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const [addVideoLinkModalOpen, setAddVideoLinkModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LibraryVideoLinkItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await libraryApi.getItems({
        type: "videoLink",
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
        scope: isClientLibrary ? "client" : "admin",
      });
      const result = res as { data: LibraryVideoLinkItem[]; total: number };
      setVideos(result.data);
      setTotalVideos(result.total);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, dateFilter, isClientLibrary]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleFilterChange = (filters: Record<string, unknown>) => {
    const dateVal = filters.updatedDate;
    setDateFilter(
      dateVal instanceof Date
        ? dateVal.toISOString()
        : (dateVal as string) || null,
    );
  };

  const handleVideoOpen = (video: LibraryVideoLinkItem) => {
    if (!video.externalUrl) {
      toast.error(t("library.videoLinks.noUrl"));
      return;
    }
    window.open(video.externalUrl, "_blank", "noopener,noreferrer");
  };

  const handleAddVideoLinkSubmit = async (payload: {
    name: string;
    url: string;
    share: "everyone" | "admins" | "custom";
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    sharedDepartmentTypes?: number[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      await libraryApi.createVideoLink({
        name: payload.name,
        url: payload.url,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
        sharedDepartmentTypes:
          payload.share === "custom" ? payload.sharedDepartmentTypes : undefined,
      });
      toast.success(t("library.videoLinks.created"));
      setAddVideoLinkModalOpen(false);
      fetchVideos();
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      toast.error(
        status === 400
          ? t("library.addVideoLinkModal.invalidUrl")
          : t("messages.error.createFailed"),
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryApi.deleteItem(deleteTarget.id);
      toast.success(t("library.videoLinks.deleted"));
      setDeleteTarget(null);
      fetchVideos();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    } finally {
      setDeleteLoading(false);
    }
  };

  const dateFilterField = {
    name: "updatedDate",
    label: t("library.filters.updatedDate"),
    type: "date" as const,
    placeholder: t("library.filters.updatedDatePlaceholder"),
  };

  const videoColumns: TableColumn<LibraryVideoLinkItem>[] = [
    {
      header: t("library.videoLinks.cols.name"),
      accessor: (row) => (
        <button
          type="button"
          className={styles.videoNameButton}
          onClick={() => handleVideoOpen(row)}
        >
          <span className={styles.fileIconWrap}>
            <PlayCircle width={20} height={20} />
          </span>
          <span>{row.name}</span>
        </button>
      ),
    },
    {
      header: t("library.videoLinks.cols.uploadedBy"),
      accessor: (row) => row.uploadedBy || "—",
    },
    {
      header: t("library.videoLinks.cols.lastUpdated"),
      accessor: (row) => row.lastUpdatedFormatted,
    },
    {
      header: t("library.videoLinks.cols.action"),
      accessor: (row) => {
        const isOpen = openDropdowns[row.id] ?? false;
        return (
          <Popover
            content={
              <div className={styles.menuDropdown}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => handleVideoOpen(row)}
                >
                  {t("library.videoLinks.open")}
                </button>
                {canDelete && !isClientLibrary && (
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      setOpenDropdowns((prev) => ({ ...prev, [row.id]: false }));
                      setDeleteTarget(row);
                    }}
                  >
                    {t("common.delete")}
                  </button>
                )}
              </div>
            }
            isOpen={isOpen}
            onClose={() => setOpenDropdowns((prev) => ({ ...prev, [row.id]: false }))}
            position="bottom"
          >
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setOpenDropdowns((prev) => ({ ...prev, [row.id]: !isOpen }));
              }}
            >
              <ActionsIcon width={20} height={20} />
            </button>
          </Popover>
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("library.title")} />

      <div className={styles.pageContainer}>
        <div className={documentsStyles.breadcrumb}>
          <button
            type="button"
            className={documentsStyles.breadcrumbLink}
            onClick={() => navigate(prefix)}
          >
            {t("library.title")}
          </button>
          <span className={documentsStyles.breadcrumbSep}>&gt;</span>
          <span className={documentsStyles.breadcrumbCurrent}>
            {t("library.videoLinks.title")}
          </span>
        </div>

        <PageHeader
          searchPlaceholder={t("library.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={[dateFilterField]}
          onFilterChange={handleFilterChange}
          onAddNew={canCreate && !isClientLibrary ? () => setAddVideoLinkModalOpen(true) : undefined}
          addButtonLabel={
            canCreate && !isClientLibrary ? t("library.addVideoLinkButton") : undefined
          }
          viewMode="list"
        />

        <div className={documentsStyles.tableWrap}>
          <Table
            columns={videoColumns}
            data={videos}
            isLoading={loading}
            getRowId={(row) => row.id}
            totalRecords={totalVideos}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setCurrentPage(1);
            }}
            onRowClick={handleVideoOpen}
            renderActions={() => null}
            hasVisibleActions={false}
            openDropdowns={openDropdowns}
            contentBasedHeight
            emptyMessage={t("library.videoLinks.noData")}
          />
        </div>

        <AddVideoLinkModal
          isOpen={addVideoLinkModalOpen}
          onClose={() => setAddVideoLinkModalOpen(false)}
          onSubmit={handleAddVideoLinkSubmit}
        />

        <ConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title={t("library.videoLinks.deleteModal.title")}
          message={t("library.videoLinks.deleteModal.message")}
          confirmText={t("common.delete")}
          cancelText={t("buttons.cancel")}
          loading={deleteLoading}
          confirmVariant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default VideosPage;
